'use client';

import { Box, Button, CircularProgress, Grid } from '@mui/material';
import { useMutation } from '@parkchirp/api-client';
import React, { FC, useCallback } from 'react';

import {
	SET_PARKER_TO_NEUTRAL,
	SYNC_PARKER_IN_ACCESS_SYSTEM,
	UPDATE_PARKER_ACCESS_SYSTEM_STATUS,
} from '../../../mutations';
import { SetShowSnackbar } from '../../../types';

interface ActionsProps {
	parkerId: string;
	suspensionStatus: string;
	terminationStatus: string;
	accessSystemStatus: string;
	setRefreshKey: (key: any) => void;
	setShowSnackbar: SetShowSnackbar;
	integration: string | null;
}

export const ActionsDetail: FC<ActionsProps> = ({
	parkerId,
	suspensionStatus,
	terminationStatus,
	accessSystemStatus,
	setRefreshKey,
	setShowSnackbar,
	integration,
}) => {
	const [updateParkerAccessSystemStatus, { loading: updateStatusLoading }] = useMutation(
		UPDATE_PARKER_ACCESS_SYSTEM_STATUS,
		{
			onCompleted: () => {
				setShowSnackbar({
					message: 'Action Updated',
					severity: 'success',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			},
		},
	);

	const [setParkerToNeutral, { loading: setNeutralLoading }] = useMutation(SET_PARKER_TO_NEUTRAL, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'Action Updated',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
		},
	});

	const [setParkerSync, { loading: setParkerSyncLoading }] = useMutation(SYNC_PARKER_IN_ACCESS_SYSTEM, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'Successfully Synced Parker In Access System',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
		},
	});

	const isTerminated = suspensionStatus === 'TERMINATED' || terminationStatus === 'TERMINATED';
	const isBlockAccess = accessSystemStatus === 'ON';
	const isReactiveAccess = accessSystemStatus === 'OFF_MANUAL';

	const handleBlockReactive = useCallback(
		async (event) => {
			event.preventDefault();
			try {
				await updateParkerAccessSystemStatus({
					variables: {
						parker_id: parkerId,
						access_system_status: isBlockAccess ? 'OFF_MANUAL' : 'ON',
					},
				});
				setRefreshKey((prevKey) => prevKey + 1);
			} catch (error) {
				setShowSnackbar({
					message: error.message,
					severity: 'error',
					autoHideDuration: 5000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		},
		[parkerId, setShowSnackbar, updateParkerAccessSystemStatus, setRefreshKey, isBlockAccess],
	);

	const handleSetToNeutral = useCallback(
		async (event) => {
			event.preventDefault();
			try {
				await setParkerToNeutral({
					variables: {
						parkerId,
					},
				});
			} catch (error) {
				setShowSnackbar({
					message: error.message,
					severity: 'error',
					autoHideDuration: 5000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		},
		[parkerId, setShowSnackbar, setParkerToNeutral],
	);

	const handleSync = useCallback(
		async (event) => {
			event.preventDefault();
			try {
				await setParkerSync({
					variables: {
						parkerId,
					},
					context: { apiVersion: 'v2' },
				});
			} catch (err) {
				let error = 'Error Syncing Parker In Access System';
				if (err.message.includes('Access level code')) {
					error = err.message;
				}
				setShowSnackbar({
					message: error,
					severity: 'error',
					autoHideDuration: 6000,
				});
			}
		},
		[parkerId, setShowSnackbar, setParkerSync],
	);

	return (
		<Grid item xs={10} sx={{ mb: 2 }}>
			<Box className='pca__parkerDetail-actions'>
				<h2>Actions</h2>
				{integration && (
					<Grid container spacing={1} sx={{ mt: 1 }}>
						{!isTerminated && (
							<>
								<Grid item xs={3}>
									<Button
										variant='contained'
										size='small'
										fullWidth
										onClick={handleBlockReactive}
										disabled={!isBlockAccess && !isReactiveAccess}
									>
										{updateStatusLoading ? (
											<CircularProgress size={23} sx={{ color: 'white' }} />
										) : isBlockAccess ? (
											'Block Access to Facility'
										) : (
											'Reactivate Access System'
										)}
									</Button>
								</Grid>
								<Grid item xs={3} onClick={handleSetToNeutral}>
									<Button variant='contained' size='small' fullWidth sx={{ ml: 2 }}>
										{setNeutralLoading ? (
											<CircularProgress size={23} sx={{ color: 'white' }} />
										) : (
											'Set Parker to Neutral'
										)}
									</Button>
								</Grid>
							</>
						)}
						<Grid item xs={3} onClick={handleSync}>
							<Button
								variant='contained'
								size='small'
								fullWidth
								sx={{ ml: 2 }}
								disabled={setParkerSyncLoading}
								style={{ marginLeft: '30px' }}
							>
								{setParkerSyncLoading ? (
									<CircularProgress size={23} sx={{ color: 'white' }} />
								) : (
									'Sync Parker In Access System'
								)}
							</Button>
						</Grid>
					</Grid>
				)}
			</Box>
		</Grid>
	);
};
